/*
 * nijikango_element_view | 50%表示で初回のみ | element_id, element_type, element_name, element_index, page_location
 * nijikango_element_tap  | 対象要素タップで連打抑止 | element_id, element_type, element_name, element_index, tap_target, page_location
 * swipe_card             | 求人カードのスワイプ | card_id, card_index, card_name, page_location
 */

const viewedElements = new Set<string>();
const tapTimestamps: Record<string, number> = {};
const TAP_DEBOUNCE_MS = 500;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    clarity?: (...args: unknown[]) => void;
  }
}

type GA4Params = Record<string, string>;

function fireGtag(eventName: string, params: GA4Params) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
}

function getPageLocation(): string {
  return window.location.href;
}

function buildViewParams(el: HTMLElement, fallbackId: string): GA4Params {
  return {
    element_id: el.dataset.elementId || fallbackId,
    element_type: el.dataset.elementType || 'unknown',
    element_name: el.dataset.elementName || '',
    element_index: el.dataset.elementIndex || '0',
    page_location: getPageLocation(),
  };
}

function buildTapParams(el: HTMLElement, fallbackId: string, tapTarget: string): GA4Params {
  return {
    element_id: el.dataset.elementId || fallbackId,
    element_type: el.dataset.elementType || 'unknown',
    element_name: el.dataset.elementName || '',
    element_index: el.dataset.elementIndex || '0',
    tap_target: tapTarget,
    page_location: getPageLocation(),
  };
}

/** nijikango_element_view – DOM要素: data-track-view="true" 付与で観測 */
export function setupViewTracking() {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          const el = entry.target as HTMLElement;
          const elementId = el.dataset.elementId || el.id || '';
          if (elementId && !viewedElements.has(elementId)) {
            viewedElements.add(elementId);
            fireGtag('nijikango_element_view', buildViewParams(el, elementId));
            observer.unobserve(el);
          }
        }
      });
    },
    { threshold: 0.5 }
  );

  document.querySelectorAll('[data-track-view="true"]').forEach((el) => {
    observer.observe(el);
  });
}

/** nijikango_element_tap – event delegation, 500ms 抑止 */
export function setupTapTracking() {
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const tracked = target.closest('[data-track-tap="true"]') as HTMLElement | null;
    if (!tracked) return;

    const elementId = tracked.dataset.elementId || '';
    if (!elementId) return;

    const now = Date.now();
    if (tapTimestamps[elementId] && now - tapTimestamps[elementId] < TAP_DEBOUNCE_MS) return;
    tapTimestamps[elementId] = now;

    const tapTarget = (target as HTMLElement).tagName?.toLowerCase() || '';
    fireGtag('nijikango_element_tap', buildTapParams(tracked, elementId, tapTarget));
  });
}

/** プログラム的にview イベントを発火（モーダル内ステップなど DOM 経由できない場合） */
export function fireViewEvent(params: {
  elementId: string;
  elementType: string;
  elementName: string;
  elementIndex: number;
}) {
  const key = params.elementId;
  if (viewedElements.has(key)) return;
  viewedElements.add(key);
  fireGtag('nijikango_element_view', {
    element_id: params.elementId,
    element_type: params.elementType,
    element_name: params.elementName,
    element_index: String(params.elementIndex),
    page_location: getPageLocation(),
  });
}

/** プログラム的にtap イベントを発火 */
export function fireTapEvent(params: {
  elementId: string;
  elementType: string;
  elementName: string;
  elementIndex: number;
  tapTarget?: string;
}) {
  const key = params.elementId;
  const now = Date.now();
  if (tapTimestamps[key] && now - tapTimestamps[key] < TAP_DEBOUNCE_MS) return;
  tapTimestamps[key] = now;
  fireGtag('nijikango_element_tap', {
    element_id: params.elementId,
    element_type: params.elementType,
    element_name: params.elementName,
    element_index: String(params.elementIndex),
    tap_target: params.tapTarget || 'button',
    page_location: getPageLocation(),
  });
}

/**
 * swipe_card – ユーザーが求人カードをスワイプして別カードに移動したときに発火。
 * 連打抑止なし（スワイプのたびに発火する）。
 */
export function fireSwipeEvent(params: {
  cardId: string | number;
  cardIndex: number;
  cardName: string;
}) {
  fireGtag('swipe_card', {
    card_id:       String(params.cardId),
    card_index:    String(params.cardIndex),
    card_name:     params.cardName,
    page_location: getPageLocation(),
  });
}

/**
 * generate_lead – 全ステップ入力完了・送信成功時に1回だけ発火するコンバージョンイベント。
 * GA4 のコンバージョン設定はこのイベント名で行う。
 */
export function fireGenerateLead(params: {
  qualification: string;
  transfer_timing: string;
  page_location?: string;
}) {
  fireGtag('generate_lead', {
    qualification:    params.qualification,
    transfer_timing:  params.transfer_timing,
    page_location:    params.page_location ?? getPageLocation(),
  });
}
